import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Clock3, X } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { queryClient } from "@/lib/query-client";
import { useBackCloseable } from "@/lib/modal-stack";
import { currency, shortDate } from "@/lib/format";
import { confirmBooking, rejectBooking } from "@/services/booking-api";
import { allowedRoomTypes, needsText, useRoomSuggestion } from "./useRoomSuggestion";
import { RoomAllotmentPicker } from "@/features/bookings/RoomAllotmentPicker";
import type { Booking } from "@/types/domain";
import type { InventoryRoom } from "@/features/inventory/types";

// Owners get a 5-minute response window; used only to size the progress bar when the
// booking doesn't carry its creation time.
const DEFAULT_WINDOW_MS = 5 * 60_000;

const sourceLabel: Record<NonNullable<Booking["source"]>, string> = {
  DIRECT_SITE: "via Darshan app",
  PHONE: "via phone",
  WALK_IN: "Walk-in",
  ADMIN_ADDED: "Added by admin",
  SAAS_ADAPTER: "via partner channel",
};

export function BookingPopupModal({ booking, onClose, position = 1, total = 1 }: { booking: Booking; onClose: () => void; position?: number; total?: number }) {
  const [now, setNow] = useState(Date.now());
  const [roomIds, setRoomIds] = useState<string[]>([]);
  // Auto-allot: the server's pick (clean rooms first, a group kept together) is pre-selected.
  const suggestion = useRoomSuggestion(booking, roomIds, setRoomIds);
  const [confirmingReject, setConfirmingReject] = useState(false);
  // Confirming is asked twice too, so a stray tap on the alert never accepts a booking.
  const [confirmingAccept, setConfirmingAccept] = useState(false);
  const roomsNeeded = booking.roomsCount ?? 1;
  const expiresAt = useMemo(() => new Date(booking.expiresAt ?? booking.ownerResponseDeadline ?? Date.now()).getTime(), [booking.expiresAt, booking.ownerResponseDeadline]);
  const windowMs = useMemo(() => {
    const created = booking.createdAt ? new Date(booking.createdAt).getTime() : 0;
    return created && expiresAt > created ? expiresAt - created : DEFAULT_WINDOW_MS;
  }, [booking.createdAt, expiresAt]);
  const remainingMs = Math.max(0, expiresAt - now);
  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);
  const remaining = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  const urgency = remainingMs < 30_000 ? "critical" : remainingMs < 120_000 ? "warning" : "normal";
  const progress = Math.min(100, Math.max(0, (remainingMs / windowMs) * 100));
  const nightsCount = Math.max(1, Math.round((new Date(booking.checkOutDate).getTime() - new Date(booking.checkInDate).getTime()) / 86_400_000));

  const settle = async () => {
    await queryClient.invalidateQueries({ queryKey: ["bookings"] });
    await queryClient.invalidateQueries({ queryKey: ["pending-bookings"] });
    window.setTimeout(onClose, 900);
  };
  const rooms = useQuery({
    queryKey: ["inventory-rooms", booking.stayProfileId, booking.checkInDate, booking.checkOutDate],
    queryFn: () => api<InventoryRoom[]>(`/inventory/rooms?propertyId=${booking.stayProfileId}&date=${booking.checkInDate.slice(0, 10)}&checkOutDate=${booking.checkOutDate.slice(0, 10)}`),
    enabled: Boolean(booking.stayProfileId) && booking.bookingStatus === "PENDING",
  });
  const types = allowedRoomTypes(booking);
  const roomOptions = (rooms.data ?? []).filter((room) => room.status === "AVAILABLE" && room.isBookable && (!types.length || (room.roomTypeId && types.includes(room.roomTypeId))));
  const confirm = useMutation({ mutationFn: () => confirmBooking(booking.id, roomIds), onSuccess: settle });
  const reject = useMutation({ mutationFn: () => rejectBooking(booking.id), onSuccess: settle });
  useBackCloseable(true, onClose);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (booking.bookingStatus !== "PENDING") {
      const closeTimer = window.setTimeout(onClose, 900);
      return () => window.clearTimeout(closeTimer);
    }
  }, [booking.bookingStatus, onClose]);

  useEffect(() => {
    if (booking.bookingStatus === "PENDING" && remainingMs <= 0) onClose();
  }, [booking.bookingStatus, remainingMs, onClose]);

  // Screen readers hear the countdown at thresholds only, not every second.
  const spokenTime = remainingMs <= 30_000 ? "Less than 30 seconds left" : remainingMs <= 120_000 ? "Less than 2 minutes left" : `${minutes} minutes left`;

  const done = confirm.isSuccess || reject.isSuccess || booking.bookingStatus !== "PENDING";
  const error = confirm.error ?? reject.error;
  const busy = confirm.isPending || reject.isPending;

  return (
    <div className="live-booking-backdrop live-booking-backdrop--blocking" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="live-booking-modal" role="dialog" aria-modal="true" aria-labelledby="live-booking-title">
        <span className="sheet-handle" aria-hidden="true" />
        {done ? (
          <div className="live-booking-done" role="status">
            <span className={clsx("live-booking-done__icon", reject.isSuccess && "is-rejected")}>{reject.isSuccess ? <X size={36} /> : <CheckCircle2 size={36} />}</span>
            <h2 id="live-booking-title">{reject.isSuccess ? "Request rejected" : confirm.isSuccess ? "Booking confirmed" : `Already ${booking.bookingStatus.toLowerCase().replace(/_/g, " ")}`}</h2>
            <p>{confirm.isSuccess ? `Rooms allotted to ${booking.guestName}.` : `${booking.guestName}'s request has been updated.`}{total > 1 ? " Next request opens in a moment." : ""}</p>
          </div>
        ) : (
          <>
            <div className="live-booking-body">
              <header className="live-booking-head">
                <span className="live-booking-tag">NEW REQUEST{total > 1 ? ` · ${position} OF ${total}` : ""}</span>
                <span className="live-booking-source">{booking.source ? sourceLabel[booking.source] : ""}</span>
                <button className="icon-button" type="button" aria-label="Remind me later" onClick={onClose}><X size={20} /></button>
              </header>

              <div className={`request-timer is-${urgency}`}>
                <Clock3 size={24} aria-hidden="true" />
                <div className="request-timer__bar">
                  <span>Respond within</span>
                  <i aria-hidden="true"><i style={{ width: `${progress}%` }} /></i>
                </div>
                <strong className="num" role="timer" aria-label={spokenTime}>{remaining}</strong>
              </div>

              <div>
                <h2 id="live-booking-title" className="live-booking-guest">{booking.guestName}</h2>
                {booking.guestPhone && <a className="live-booking-phone num" href={`tel:${booking.guestPhone}`}>{booking.guestPhone}</a>}
              </div>

              <dl className="request-facts">
                <div><dt>Stay</dt><dd className="num">{shortDate(booking.checkInDate)} → {shortDate(booking.checkOutDate)}</dd><dd className="muted">{nightsCount} night{nightsCount === 1 ? "" : "s"}</dd></div>
                <div><dt>Amount</dt><dd className="num live-booking-amount">{currency.format(Number(booking.totalAmount))}</dd></div>
                <div><dt>Rooms</dt><dd>{needsText(booking)}</dd></div>
                <div><dt>Guests</dt><dd>{booking.adults ?? booking.noOfGuests} adult(s){booking.children ? `, ${booking.children} child(ren)` : ""}</dd></div>
              </dl>

              <RoomAllotmentPicker roomsNeeded={roomsNeeded} options={roomOptions} selected={roomIds} loading={rooms.isLoading} onChange={setRoomIds} suggested={suggestion.data?.roomIds} showType={(booking.roomTypeLines?.length ?? 0) > 1} />

              {error && <div className="form-alert" role="alert"><AlertCircle size={18} /><span>{error.message}. Please try again.</span></div>}
            </div>

            <footer className="live-booking-actions">
              {confirmingReject ? (
                <>
                  <Button variant="secondary" disabled={busy} onClick={() => setConfirmingReject(false)}>Keep request</Button>
                  <Button variant="danger" className="live-booking-actions__main" loading={reject.isPending} disabled={confirm.isPending} onClick={() => reject.mutate()}>Yes, reject</Button>
                </>
              ) : confirmingAccept ? (
                <>
                  <Button variant="secondary" disabled={busy} onClick={() => setConfirmingAccept(false)}>Back</Button>
                  <Button className="live-booking-actions__main" loading={confirm.isPending} disabled={reject.isPending || roomIds.length !== roomsNeeded} onClick={() => confirm.mutate()}>Yes, confirm for {booking.guestName}</Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" className="button--danger-outline" disabled={busy} onClick={() => setConfirmingReject(true)}>Reject</Button>
                  <Button className="live-booking-actions__main" disabled={reject.isPending || roomIds.length !== roomsNeeded} onClick={() => setConfirmingAccept(true)}>Confirm booking</Button>
                </>
              )}
            </footer>
          </>
        )}
      </section>
    </div>
  );
}
